/**
 * Side effects when a change order is approved (customer-signed).
 *
 * Mirrors — but deliberately does NOT reuse — `syncSignedDealSideEffects`:
 * a change order must update the contract value and create its own sales order,
 * but must never re-mark the deal won, jump portal stages, or create a duplicate
 * base sales order.
 */

import { setDoc, doc, getDoc, getDocs, query, where, Timestamp, addDoc, collection, updateDoc } from 'firebase/firestore';
import { db } from './firebase';
import type {
  ChangeOrder,
  ChangeOrderAuditEntry,
  ProjectPLBucket,
  ProposalBuilderData,
  ProposalNodeSet,
  SalesOrder,
  SalesOrderLineItem,
} from '../types';
import {
  recomputeAndSyncContractValue,
  computePriorContractValueBeforeChangeOrder,
  loadChangeOrderLedgerContext,
} from './contractValue';
import { buildChangeOrderSnapshot, type ChangeOrderSnapshot } from './changeOrderSnapshot';
import { getProposalBucketBudget } from './projectPL';
import { uploadApprovedChangeOrderPdf } from './changeOrderPdfUpload';
import { calculateTotals } from './proposalCalculations';
import { updateChangeOrder, getChangeOrderReference } from './changeOrders';
import { markPhaseCommitted } from './proposalPhases';
import { removeUndefinedDeep } from './removeUndefinedDeep';
import { changeOrderBillsImmediately } from './paymentSchedule';
import { recordPaymentForecastSnapshotForDeal } from './paymentForecastLog';

const TIMBER_KIT_BUCKETS: ProjectPLBucket[] = ['timber', 'production', 'freight'];
const INSTALL_BUCKETS: ProjectPLBucket[] = ['install', 'travel', 'overhead'];

function builderStructureLineItem(ns: ProposalNodeSet, margin: number): SalesOrderLineItem {
  const totals = calculateTotals([ns], margin);
  // Cost fields are assigned by the caller (billable, split across buckets).
  return {
    structureId: ns.id,
    structureName: ns.name || 'Structure',
    quantity: ns.quantity || 1,
    species: '',
    designCost: 0,
    timberKitCost: 0,
    installCost: 0,
    subtotal: Math.round(totals.billableTotal),
  };
}

/** Build sales-order line items + bucket totals from a change order. */
export function buildChangeOrderSalesOrderLines(co: ChangeOrder): {
  lineItems: SalesOrderLineItem[];
  designTotal: number;
  timberKitTotal: number;
  installTotal: number;
} {
  let designTotal = 0;
  let timberKitTotal = 0;
  let installTotal = 0;
  const lineItems: SalesOrderLineItem[] = [];

  if (co.pricingMode === 'line_item' && co.lineItems) {
    co.lineItems.forEach(li => {
      const designCost = li.bucket === 'design' ? li.amount : 0;
      const timberKitCost = TIMBER_KIT_BUCKETS.includes(li.bucket) ? li.amount : 0;
      const installCost = INSTALL_BUCKETS.includes(li.bucket) ? li.amount : 0;
      designTotal += designCost;
      timberKitTotal += timberKitCost;
      installTotal += installCost;
      lineItems.push({
        structureId: li.id,
        structureName: li.description || 'Change order item',
        quantity: li.quantity || 1,
        species: '',
        designCost,
        timberKitCost,
        installCost,
        subtotal: li.amount,
      });
    });
  } else if (co.pricingMode === 'builder' && co.builderNodeSets) {
    const margin = co.builderProfitMargin ?? 0;
    co.builderNodeSets
      .filter(ns => ns.enabled !== false)
      .forEach(ns => {
        const li = builderStructureLineItem(ns, margin);
        // billable buckets via margin so they sum to subtotal
        const totals = calculateTotals([ns], margin);
        const rawTotal = totals.rawCostTotal || 1;
        const billable = totals.billableTotal;
        const designBillable = Math.round((totals.designTotal / rawTotal) * billable);
        const timberBillable = Math.round((totals.timberKitTotal / rawTotal) * billable);
        const installBillable = billable - designBillable - timberBillable;
        li.designCost = designBillable;
        li.timberKitCost = timberBillable;
        li.installCost = installBillable;
        designTotal += designBillable;
        timberKitTotal += timberBillable;
        installTotal += installBillable;
        lineItems.push(li);
      });
  } else {
    // lump sum — single summary line.
    timberKitTotal = co.deltaAmount;
    lineItems.push({
      structureId: co.id || 'lump',
      structureName: co.title || 'Change order',
      quantity: 1,
      species: '',
      designCost: 0,
      timberKitCost: co.deltaAmount,
      installCost: 0,
      subtotal: co.deltaAmount,
    });
  }

  return { lineItems, designTotal, timberKitTotal, installTotal };
}

/**
 * Deterministic sales order doc id — one CO → one sales order → one onCreate trigger.
 */
export function changeOrderSalesOrderDocId(coId: string): string {
  return `co-${coId}`;
}

/**
 * Create the per-change-order sales order exactly once, returning its id.
 *
 * Uses a deterministic Firestore doc id (`co-{changeOrderId}`) so parallel callers
 * (public sign, onChangeOrderApproved, panel backfill) cannot create duplicates.
 * Creating the doc triggers `onSalesOrderCreated`, which creates the QB invoice.
 */
export async function ensureChangeOrderSalesOrder(coId: string): Promise<string | null> {
  const snap = await getDoc(doc(db, 'changeOrders', coId));
  if (!snap.exists()) return null;
  const co = { id: snap.id, ...(snap.data() as ChangeOrder) };
  if (co.salesOrderId) return co.salesOrderId;

  // Contract-terms COs (incl. all credits) fold into the payment schedule —
  // no sales order or QB invoice is ever created for them.
  if (!changeOrderBillsImmediately(co)) return null;

  const soDocId = changeOrderSalesOrderDocId(coId);
  const soRef = doc(db, 'salesOrders', soDocId);
  const existingSo = await getDoc(soRef);
  if (existingSo.exists()) {
    await updateChangeOrder(co.id!, { salesOrderId: soDocId });
    return soDocId;
  }

  const approvedAt = co.approvedAt
    ? co.approvedAt instanceof Date
      ? co.approvedAt
      : (co.approvedAt as unknown as { toDate?: () => Date }).toDate?.() || new Date()
    : co.signature?.signedAt
      ? co.signature.signedAt instanceof Date
        ? co.signature.signedAt
        : new Date(co.signature.signedAt as unknown as string)
      : new Date();

  const signedBy = co.signature?.signedBy
    || (co.internalApproval ? `${co.internalApprovedBy || 'Staff'} (internal)` : 'Customer');

  try {
    const { lineItems, designTotal, timberKitTotal, installTotal } = buildChangeOrderSalesOrderLines(co);
    const salesOrder: Omit<SalesOrder, 'id'> = {
      proposalId: co.proposalId || co.dealId,
      dealId: co.dealId,
      clientName: co.clientName || 'Unknown',
      signedAt: approvedAt,
      signedBy,
      signatureDataUrl: co.signature?.dataUrl || '',
      includeInstall: installTotal !== 0,
      selectedSpecies: {},
      designTotal,
      timberKitTotal,
      installTotal,
      grandTotal: co.deltaAmount,
      profitMargin: co.builderProfitMargin ?? 0,
      lineItems,
      // Change-order linkage — keeps Won Deals / QB invoicing additive.
      changeOrderId: co.id,
      isChangeOrder: true,
      createdAt: new Date(),
    };
    const ref = soRef;
    await setDoc(ref, salesOrder);
    await updateChangeOrder(co.id!, { salesOrderId: soDocId });

    return soDocId;
  } catch (err) {
    console.error('[changeOrderSync] sales order creation failed', err);
    throw err;
  }
}

/**
 * Phased projects: when an approved CO is the vehicle for a phase commitment
 * (proposal phase carries `changeOrderId`), stamp the phase `committed` so the
 * proposal page / portal / management panel all reflect it — regardless of
 * whether the client signed on the proposal page or the standalone CO page.
 */
export async function markLinkedProposalPhaseCommitted(co: ChangeOrder): Promise<string | null> {
  if (!co.id) return null;
  const proposalId = co.proposalId || co.dealId;
  if (!proposalId) return null;
  const snap = await getDoc(doc(db, 'proposalBuilders', proposalId));
  if (!snap.exists()) return null;
  const proposal = snap.data() as ProposalBuilderData;
  const phases = proposal.phases || [];
  const target = phases.find(p => p.changeOrderId === co.id && p.status !== 'committed');
  if (!target) return null;

  const committedBy =
    co.signature?.signedBy || (co.internalApproval ? `${co.internalApprovedBy || 'Staff'} (internal)` : 'Client');
  const updated = markPhaseCommitted(phases, target.id, {
    committedBy,
    signature: co.signature
      ? {
          dataUrl: co.signature.dataUrl,
          signedBy: co.signature.signedBy,
          signedAt:
            co.signature.signedAt instanceof Date
              ? co.signature.signedAt
              : (co.signature.signedAt as unknown as { toDate?: () => Date })?.toDate?.() || new Date(),
        }
      : undefined,
    changeOrderId: co.id,
  });
  await updateDoc(doc(db, 'proposalBuilders', proposalId), {
    phases: removeUndefinedDeep(updated),
    updatedAt: Timestamp.now(),
  });
  return target.name || null;
}

/**
 * Ensure the deal's Project Hub project has a task group named after a
 * committed phase. Best-effort: called from CO approval flows (where
 * CRMContext is unavailable) and backfilled from the staff phase panel —
 * public clients lack permission on timberProjects/taskGroups, so commits
 * signed on the public page get their group on the next staff view.
 */
export async function ensurePhaseTaskGroup(dealId: string, phaseName: string): Promise<void> {
  const name = (phaseName || '').trim();
  if (!name || !dealId) return;

  const projSnap = await getDocs(query(collection(db, 'timberProjects'), where('dealId', '==', dealId)));
  if (projSnap.empty) return;
  const projects = projSnap.docs.map(d => ({ id: d.id, phase: (d.data() as { phase?: string }).phase }));
  const project = projects.find(p => p.phase === 'contracted') || projects[0];
  if (!project?.id) return;

  const groupsSnap = await getDocs(query(collection(db, 'taskGroups'), where('projectId', '==', project.id)));
  const existing = groupsSnap.docs.map(d => d.data() as { name?: string; position?: number });
  if (existing.some(g => (g.name || '').trim().toLowerCase() === name.toLowerCase())) return;

  const maxPosition = existing.reduce((max, g) => Math.max(max, typeof g.position === 'number' ? g.position : 0), 0);
  const reservedId = `tg-phase-${project.id}-${name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`;
  await setDoc(doc(db, 'taskGroups', reservedId), {
    projectId: project.id,
    name,
    color: '#8B5CF6',
    position: maxPosition + 1,
    collapsed: false,
    createdAt: Timestamp.now(),
  }, { merge: true });
}

/**
 * Stamp the approval snapshot (contract + bucket budget before/after) on an
 * approved change order, once. Later COs can then never move what this CO
 * showed the client. No-op when already stamped or not approved. Anonymous
 * signers cannot write these keys — `onChangeOrderApproved` stamps server-side
 * in that case — so permission errors are swallowed.
 */
export async function stampChangeOrderSnapshot(coId: string): Promise<ChangeOrderSnapshot | null> {
  const snap = await getDoc(doc(db, 'changeOrders', coId));
  if (!snap.exists()) return null;
  const co = { id: snap.id, ...(snap.data() as ChangeOrder) };
  if (co.status !== 'approved') return null;
  if (typeof co.contractValueBefore === 'number' && Number.isFinite(co.contractValueBefore)) {
    return {
      contractValueBefore: co.contractValueBefore,
      contractValueAfter: co.contractValueAfter ?? co.contractValueBefore + (Number(co.deltaAmount) || 0),
      budgetByBucketBefore: co.budgetByBucketBefore || {},
      budgetByBucketAfter: co.budgetByBucketAfter || {},
    };
  }

  const ctx = await loadChangeOrderLedgerContext(co.dealId);
  const all = ctx.changeOrders.some(c => c.id === co.id)
    ? ctx.changeOrders.map(c => (c.id === co.id ? co : c))
    : [...ctx.changeOrders, co];
  const proposalBudget: Partial<Record<ProjectPLBucket, number>> = {};
  const full = getProposalBucketBudget(ctx.proposal);
  (Object.keys(full) as ProjectPLBucket[]).forEach(bucket => {
    if (full[bucket]) proposalBudget[bucket] = Math.round(full[bucket]);
  });
  const snapshot = buildChangeOrderSnapshot(co, ctx.base, proposalBudget, all);

  try {
    await updateDoc(doc(db, 'changeOrders', coId), {
      ...snapshot,
      snapshotAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    console.warn('[changeOrderSync] snapshot stamp deferred to server', err);
  }
  return snapshot;
}

export interface SyncApprovedChangeOrderDeps {
  userId?: string;
  updateEnhancedDeal?: (id: string, updates: { value: number }) => Promise<void>;
  ensureProcurementTaskGroupForProject?: (projectId: string) => Promise<void>;
}

/**
 * Apply an approved change order: recompute contract value, ensure procurement,
 * and log CRM activity. Sales order + QB invoice are created server-side by
 * onChangeOrderApproved (not here — avoids duplicate orders/invoices).
 */
export async function syncApprovedChangeOrder(
  coId: string,
  deps?: SyncApprovedChangeOrderDeps,
): Promise<void> {
  const snap = await getDoc(doc(db, 'changeOrders', coId));
  if (!snap.exists()) return;
  const co = { id: snap.id, ...(snap.data() as ChangeOrder) };
  if (co.status !== 'approved') return;

  // 0. Freeze what this CO did to the contract and budget before anything else moves.
  try {
    await stampChangeOrderSnapshot(coId);
  } catch (err) {
    console.warn('[changeOrderSync] snapshot stamp failed', err);
  }

  // 1. Recompute the deal's contract value (cascades to timber project value).
  await recomputeAndSyncContractValue(co.dealId, { updateEnhancedDeal: deps?.updateEnhancedDeal });

  // 1b. Payment forecast audit — both billing modes change the schedule
  //     (folded COs adjust installment amounts; immediate COs add a line).
  void recordPaymentForecastSnapshotForDeal(co.dealId, 'co_approved', {
    byUid: deps?.userId,
    changeOrderId: co.id,
  });

  // 2. Sales order + QB invoice — server-only via onChangeOrderApproved →
  //    ensureChangeOrderSalesOrderAdmin (deterministic doc id). Panel backfill
  //    calls ensureChangeOrderSalesOrder for older approved COs missing an order.

  // 2b. Phased projects — commit the linked proposal phase (no-op otherwise).
  try {
    const committedPhaseName = await markLinkedProposalPhaseCommitted(co);
    if (committedPhaseName && co.dealId) {
      try {
        await ensurePhaseTaskGroup(co.dealId, committedPhaseName);
      } catch (err) {
        console.warn('[changeOrderSync] phase task group ensure failed', err);
      }
    }
  } catch (err) {
    console.warn('[changeOrderSync] phase commit stamp failed', err);
  }

  // 3. Ensure a procurement task group when the CO carries materials/scope.
  if (deps?.ensureProcurementTaskGroupForProject && co.projectId) {
    try {
      await deps.ensureProcurementTaskGroupForProject(co.projectId);
    } catch (err) {
      console.warn('[changeOrderSync] procurement task group ensure failed', err);
    }
  }

  // 4. CRM activity.
  try {
    await addDoc(collection(db, 'activities'), {
      type: 'note',
      title: `Change Order Approved — ${getChangeOrderReference(co)}`,
      description: `${co.internalApproval
        ? `${co.internalApprovedBy || 'Staff'} (internal approval)`
        : co.signature?.signedBy || 'Customer'} approved ${getChangeOrderReference(co)} (${co.title}) for ${co.deltaAmount >= 0 ? '+' : ''}$${Math.abs(Math.round(co.deltaAmount)).toLocaleString('en-US')}.`,
      entityType: 'deal',
      entityId: co.dealId,
      entityName: co.clientName,
      status: 'completed',
      completedAt: Timestamp.now(),
      createdBy: deps?.userId || 'system',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
  } catch (err) {
    console.warn('[changeOrderSync] activity log failed', err);
  }
}

export interface ApproveChangeOrderInternallyOptions extends SyncApprovedChangeOrderDeps {
  userName: string;
  /** Must be true — enforced in UI and Firestore rules (admin only). */
  isAdmin: boolean;
}

/**
 * Admin-only path: record client approval without a portal signature.
 * Updates status, audit trail, contract value, sales order, and CRM activity.
 */
export async function approveChangeOrderInternally(
  coId: string,
  opts: ApproveChangeOrderInternallyOptions,
): Promise<void> {
  if (!opts.isAdmin) {
    throw new Error('Only admins can approve change orders internally');
  }
  const snap = await getDoc(doc(db, 'changeOrders', coId));
  if (!snap.exists()) throw new Error('Change order not found');
  const co = { id: snap.id, ...(snap.data() as ChangeOrder) };

  if (co.status === 'approved') {
    throw new Error('Change order is already approved');
  }
  if (co.status === 'void' || co.status === 'rejected') {
    throw new Error('Cannot approve a void or rejected change order');
  }

  const approvedAt = new Date();
  const audit: ChangeOrderAuditEntry = {
    at: approvedAt,
    byUid: opts.userId || 'staff',
    byName: opts.userName,
    action: 'internal_approved',
    fromStatus: co.status,
    toStatus: 'approved',
    amount: co.deltaAmount,
  };

  await updateChangeOrder(coId, {
    status: 'approved',
    approvedAt,
    internalApproval: true,
    internalApprovedBy: opts.userName,
    internalApprovedByUid: opts.userId,
    auditLog: [...(co.auditLog || []), audit],
  });

  await syncApprovedChangeOrder(coId, opts);

  try {
    const updatedSnap = await getDoc(doc(db, 'changeOrders', coId));
    if (updatedSnap.exists()) {
      const updated = { id: updatedSnap.id, ...(updatedSnap.data() as ChangeOrder) };
      const prior = await computePriorContractValueBeforeChangeOrder(updated);
      const signedPdfUrl = await uploadApprovedChangeOrderPdf(updated, prior);
      if (signedPdfUrl) {
        await updateChangeOrder(coId, { signedPdfUrl });
      }
    }
  } catch (pdfErr) {
    console.warn('[changeOrderSync] internal approval PDF failed', pdfErr);
  }
}
